import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import Portal from '@mui/material/Portal';
import { makeStyles } from 'tss-react/mui';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useChatDock, MIN_DRAWER_WIDTH, type ChatViewMode } from 'components/ChatLauncher/ChatDockContext';
import { getChatPanelZIndex } from './chatPanelLayering';
import {
  clampPanelPosition,
  getChatPanelRect,
  type PanelPlacement,
  type PanelPosition,
  type PanelRect,
  type PanelSize,
} from './chatPanelGeometry';

interface DraggablePopperProps {
  anchorEl: HTMLButtonElement | null;
  open: boolean;
  placement?: PanelPlacement;
  children: React.ReactNode;
  isMinimized?: boolean;
  viewMode: ChatViewMode;
  /** Element that is allowed to start a drag (e.g. header bar). */
  dragHandleRef?: React.RefObject<HTMLElement | null>;
}

interface PanelStyle extends React.CSSProperties {
  '--chat-left': string;
  '--chat-top': string;
  '--chat-width': string;
  '--chat-height': string;
}

type Interaction =
  | { kind: 'drag'; x: number; y: number; position: PanelPosition }
  | { kind: 'resize'; x: number; rect: PanelRect; currentWidth: number };

interface StyleProps {
  viewMode: ChatViewMode;
  visible: boolean;
  dragging: boolean;
  resizing: boolean;
  windowResizing: boolean;
  hasDragHandle: boolean;
}

const useStyles = makeStyles<StyleProps>({ name: 'DraggablePopper' })(
  (theme, { viewMode, visible, dragging, resizing, windowResizing, hasDragHandle }) => ({
    layer: {
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: getChatPanelZIndex(theme, viewMode),
      display: visible ? 'block' : 'none',
    },
    backdrop: { position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'auto' },
    surface: {
      position: 'absolute',
      left: 'var(--chat-left)',
      top: 'var(--chat-top)',
      width: 'var(--chat-width)',
      height: 'var(--chat-height)',
      zIndex: 1,
      pointerEvents: 'auto',
      cursor: viewMode === 'floating' && !hasDragHandle ? (dragging ? 'grabbing' : 'grab') : 'auto',
      // One geometry owner and one timing curve. Children fill this surface;
      // neither content measurements nor another positioning engine move it later.
      transitionProperty: windowResizing || resizing ? 'none' : dragging ? 'width, height' : 'left, top, width, height',
      transitionDuration: '300ms',
      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    },
    resizeHandle: {
      display: viewMode === 'docked' ? 'block' : 'none',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: theme.spacing(0.5),
      cursor: 'ew-resize',
      zIndex: 30,
      backgroundColor: theme.palette.divider,
      transition: 'width 0.2s ease-in-out, background-color 0.2s ease-in-out',
      pointerEvents: 'auto',
      '&:hover': { backgroundColor: theme.palette.action.hover, width: 8 },
      ...(resizing && { backgroundColor: theme.palette.primary.main, opacity: 0.9, width: 8 }),
    },
  }),
);

const WINDOW_RESIZE_SETTLE_MS = 150;

function getViewport(): PanelSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

export const DraggablePopper = ({
  anchorEl,
  open,
  placement = 'bottom',
  children,
  isMinimized,
  viewMode,
  dragHandleRef,
}: DraggablePopperProps): React.JSX.Element | null => {
  const [viewport, setViewport] = useState<PanelSize>(getViewport);
  const [floatingPosition, setFloatingPosition] = useState<PanelPosition | null>(null);
  const [resizeRect, setResizeRect] = useState<PanelRect | null>(null);
  const [interactionKind, setInteractionKind] = useState<Interaction['kind'] | null>(null);
  const [windowResizing, setWindowResizing] = useState(false);
  const interactionRef = useRef<Interaction | null>(null);
  const bodyStyleRef = useRef<{ userSelect: string; cursor: string } | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const { drawerWidth, setDrawerWidth } = useChatDock();
  const { classes, theme } = useStyles({
    viewMode,
    visible: open && !isMinimized,
    dragging: interactionKind === 'drag' && viewMode === 'floating',
    resizing: interactionKind === 'resize' && viewMode === 'docked',
    windowResizing,
    hasDragHandle: Boolean(dragHandleRef),
  });
  const rightPadding = parseFloat(theme.spacing(3));

  const restoreBodyStyle = useCallback((): void => {
    if (bodyStyleRef.current) {
      document.body.style.userSelect = bodyStyleRef.current.userSelect;
      document.body.style.cursor = bodyStyleRef.current.cursor;
      bodyStyleRef.current = null;
    }
    interactionRef.current = null;
  }, []);

  const endInteraction = useCallback((): void => {
    const interaction = interactionRef.current;
    if (interaction?.kind === 'resize') {
      const maxWidth = window.innerWidth * 0.8;
      setDrawerWidth(Math.min(maxWidth, Math.max(Math.min(MIN_DRAWER_WIDTH, maxWidth), interaction.currentWidth)));
    }
    restoreBodyStyle();
    setInteractionKind(null);
    setResizeRect(null);
  }, [restoreBodyStyle, setDrawerWidth]);

  useEffect(() => {
    if (!open) setFloatingPosition(null);
  }, [open]);

  useLayoutEffect(() => {
    endInteraction();
  }, [viewMode, open, isMinimized, endInteraction]);

  useEffect(() => {
    const updateViewport = (): void => setViewport(getViewport());
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    // Follow window resizes without the transition: restarting it on every resize event leaves the surface trailing.
    const handleResize = (): void => {
      updateViewport();
      setWindowResizing(true);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => setWindowResizing(false), WINDOW_RESIZE_SETTLE_MS);
    };
    window.addEventListener('resize', handleResize);
    // Launcher anchoring matters only until the user places the floating panel.
    window.addEventListener('scroll', updateViewport, true);
    return () => {
      clearTimeout(settleTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', updateViewport, true);
    };
  }, []);

  useEffect(() => {
    const move = (event: MouseEvent): void => {
      const interaction = interactionRef.current;
      if (!interaction) return;
      if (interaction.kind === 'resize') {
        const requestedWidth = interaction.rect.width + interaction.x - event.clientX;
        // An interrupted mode animation may start outside the normal resize limits.
        // Keep that starting size reachable; settle into the normal bounds on release.
        const maxWidth = Math.max(interaction.rect.width, window.innerWidth * 0.8);
        const minWidth = Math.min(MIN_DRAWER_WIDTH, interaction.rect.width, maxWidth);
        const width = Math.max(minWidth, Math.min(requestedWidth, maxWidth));
        interaction.currentWidth = width;
        setResizeRect({ ...interaction.rect, left: interaction.rect.left + interaction.rect.width - width, width });
        setDrawerWidth(width);
      } else {
        const rect = surfaceRef.current?.getBoundingClientRect();
        if (!rect) return;
        setFloatingPosition(
          clampPanelPosition(
            {
              left: interaction.position.left + event.clientX - interaction.x,
              top: interaction.position.top + event.clientY - interaction.y,
            },
            rect,
            getViewport(),
            rightPadding,
          ),
        );
      }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', endInteraction);
    window.addEventListener('blur', endInteraction);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', endInteraction);
      window.removeEventListener('blur', endInteraction);
      restoreBodyStyle();
    };
  }, [rightPadding, setDrawerWidth, endInteraction, restoreBodyStyle]);

  const startInteraction = (interaction: Interaction): void => {
    restoreBodyStyle();
    bodyStyleRef.current = { userSelect: document.body.style.userSelect, cursor: document.body.style.cursor };
    document.body.style.userSelect = 'none';
    if (interaction.kind === 'resize') document.body.style.cursor = 'ew-resize';
    interactionRef.current = interaction;
    setInteractionKind(interaction.kind);
  };

  const handleMouseDown = (event: React.MouseEvent): void => {
    if (viewMode !== 'floating' || event.button !== 0) return;
    if (dragHandleRef?.current && (!(event.target instanceof Node) || !dragHandleRef.current.contains(event.target)))
      return;
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    const position = { left: rect.left, top: rect.top };
    // If a mode animation is still running, start from its current visible position.
    setFloatingPosition(position);
    startInteraction({ kind: 'drag', x: event.clientX, y: event.clientY, position });
  };

  const handleResizeMouseDown = (event: React.MouseEvent): void => {
    if (viewMode !== 'docked' || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const visible = surfaceRef.current?.getBoundingClientRect();
    if (!visible) return;
    const rect = { left: visible.left, top: visible.top, width: visible.width, height: visible.height };
    setResizeRect(rect);
    startInteraction({
      kind: 'resize',
      x: event.clientX,
      rect,
      currentWidth: rect.width,
    });
  };

  if (!open && viewMode === 'floating') return null;
  const targetRect = getChatPanelRect({
    viewMode,
    viewport,
    drawerWidth,
    anchor: anchorEl?.getBoundingClientRect() ?? null,
    placement,
    floatingPosition,
    rightPadding,
  });
  const rect =
    viewMode === 'docked' && interactionKind === 'resize' && resizeRect
      ? resizeRect
      : viewMode === 'floating' && interactionKind === 'drag' && floatingPosition
        ? { ...targetRect, ...floatingPosition }
        : targetRect;
  const style: PanelStyle = {
    '--chat-left': `${rect.left}px`,
    '--chat-top': `${rect.top}px`,
    '--chat-width': `${rect.width}px`,
    '--chat-height': `${rect.height}px`,
  };

  return (
    <Portal>
      <Box className={classes.layer}>
        <Backdrop open={viewMode === 'expanded' && open && !isMinimized} className={classes.backdrop} />
        <Box role='tooltip' ref={surfaceRef} className={classes.surface} style={style} onMouseDown={handleMouseDown}>
          {children}
          <Box
            role='separator'
            aria-label='Resize chat'
            aria-orientation='vertical'
            onMouseDown={handleResizeMouseDown}
            className={classes.resizeHandle}
          />
        </Box>
      </Box>
    </Portal>
  );
};
